import { useRouter } from "next/router";
import { useCallback, useEffect } from "react";

import { Editor } from "@tiptap/react";

import useChecksForUnderstandingContext from "@/assignments/useChecksForUnderstandingSubmissionsContext/useChecksForUnderstandingSubmissionsContext";
import { LANG } from "@/constants/Language";
import {
  Assignment,
  EmptyAssignment,
} from "@/types/models/Assignment.interface";
import { UserProperties } from "@/types/models/UserProperties.interface";
import useGetUserProperties from "@/users/v3/useGetUserProperties";
import useReadAloudMode from "@/view/ReadAloudControls/useReadAloudMode";
import { dispatchDataTransaction } from "@/view/TiptapArticleBody/extensions/DataProvider";
import { INode } from "@/view/TiptapArticleBody/TiptapArticleBody.interface";
import getChecksForUnderstandingQuizAttachment from "@/view/utils/getChecksForUnderstandingQuizAttachment";
import useAnnotationsState from "@/view/utils/useAnnotationsState";
import useAssignmentReducerContext from "@/view/utils/useAssignmentReducerContext";
import useAttachmentsContext from "@/view/utils/useAttachmentsContext";
import useGuidedHighlightingStudent from "@/view/utils/useGuidedHighlightingStudent";
import { IContent } from "@/view/utils/useSetInitialLevel";
import useStudentPreviewContext from "@/view/utils/useStudentPreviewContext";
import useTranslatorControlsContext from "@/view/utils/useTranslatorControlsContext";

interface UseDataTransactionsProps {
  editor: Editor | null;
  currentFontSize: string;
  media: object;
  levelId: string;
  rawText: INode | null;
  legacyArticleId?: number | undefined;
  content?: IContent;
  language: LANG;
  lexile?: string | undefined;
}

export function useDataTransactions({
  editor,
  currentFontSize,
  media,
  levelId,
  rawText,
  legacyArticleId,
  content,
  language,
  lexile,
}: UseDataTransactionsProps) {
  const router = useRouter();
  const userProperties = useGetUserProperties() as UserProperties;
  const activityPanel = `${router.query["activityPanel"] || ""}`;
  const { updatedAssignment: assignment } = useAssignmentReducerContext() as {
    updatedAssignment: Assignment | EmptyAssignment;
  };

  // Listen Mode
  const {
    isReadAloudModeActive,
    hasReadAloudInSession,
    setIsReadAloudModeActive,
    isPlaying: isListenModeActive,
  } = useReadAloudMode();

  // Annotations
  const { annotationsIncludingPossibleDraftAnnotation: annotations } =
    useAnnotationsState();

  // Guided Highlighting
  const { activeGuidedHighlightingQuestion } = useGuidedHighlightingStudent();

  // Checks For Understanding
  const {
    checksForUnderstandingSubmissions,
    addChecksForUnderstandingSubmissions,
  } = useChecksForUnderstandingContext();
  const { attachments } = useAttachmentsContext();
  const checksForUnderstandingQuiz =
    getChecksForUnderstandingQuizAttachment(attachments);

  const { isStudentPreviewOn } = useStudentPreviewContext();

  // Translate tool
  const { isTranslateMode } = useTranslatorControlsContext();

  // Dynamic data that when changed, must not cause editor state re-initialization.
  // Wrap the editor update logic in useCallback to avoid unnecessary re-creations
  const updateEditorData = useCallback(() => {
    if (editor) {
      dispatchDataTransaction(editor, {
        activeGuidedHighlightingQuestion,
        activityPanel,
        addChecksForUnderstandingSubmissions,
        annotations,
        assignment,
        checksForUnderstandingQuiz,
        checksForUnderstandingSubmissions,
        currentFontSize,
        hasReadAloudInSession,
        isListenModeActive,
        isReadAloudModeActive,
        isStudentPreviewOn,
        isTranslateMode,
        legacyArticleId,
        levelId,
        media,
        rawText,
        setIsReadAloudModeActive,
        userProperties,
        content,
        language,
        lexile,
      });
    }
  }, [
    JSON.stringify(activeGuidedHighlightingQuestion),
    activityPanel,
    addChecksForUnderstandingSubmissions,
    annotations,
    JSON.stringify(assignment),
    JSON.stringify(checksForUnderstandingQuiz),
    JSON.stringify(checksForUnderstandingSubmissions),
    currentFontSize,
    editor,
    hasReadAloudInSession,
    isListenModeActive,
    isReadAloudModeActive,
    isStudentPreviewOn,
    isTranslateMode,
    legacyArticleId,
    levelId,
    media,
    rawText,
    setIsReadAloudModeActive,
    JSON.stringify(userProperties),
    lexile,
  ]);

  // Effect to trigger the update when dependencies change
  useEffect(() => {
    updateEditorData();
  }, [updateEditorData]);

  return updateEditorData;
}
